import { useState, useEffect, useRef } from "react";
import {
  supervisedTemplates,
  unsupervisedTemplates,
  pytorchSupervisedTemplates,
  pytorchUnsupervisedTemplates,
} from "../components/codebase/CodeTemplates";
import codebaseService from "../services/codebaseService";
import { useUnsavedChanges } from "../components/editor/UnsavedChangesContext";

/**
 * Custom hook to manage code editor state and logic
 * Handles:
 * - Code content state and saving (both local and backend)
 * - Framework and mode selection
 * - Auto-save with timeout
 * - Editor refs and Monaco editor instances
 * - Section collapse/fullscreen state
 * - Template initialization and reset
 */
export const useCodeEditor = (codebaseId, userId) => {
  // Core editor state
  const [content, setContent] = useState({});
  const [mode, setMode] = useState(
    sessionStorage.getItem("mode") || "supervised"
  );
  const [framework, setFramework] = useState(
    sessionStorage.getItem("framework") || "tensorflow"
  );
  const [saveStatus, setSaveStatus] = useState("saved");
  const [collapsedSections, setCollapsedSections] = useState({});
  const [fullscreenSection, setFullscreenSection] = useState(null);

  // Refs for editor instances and timeouts
  const editorRefs = useRef({});
  const monacoRefs = useRef({});
  const backendSaveTimeoutRef = useRef(null);

  const { setHasUnsavedChanges } = useUnsavedChanges();

  // Select templates based on framework and mode
  const getTemplates = (fw, learningMode) => {
    if (fw === "pytorch") {
      return learningMode === "supervised"
        ? pytorchSupervisedTemplates
        : pytorchUnsupervisedTemplates;
    }
    return learningMode === "supervised"
      ? supervisedTemplates
      : unsupervisedTemplates;
  };

  const currentTemplates = getTemplates(framework, mode);

  // Parse code from backend format into sections
  const parseBackendCode = (code) => {
    const sections = code.split("# Section: ");
    const parsedCode = {};

    sections.forEach((section) => {
      if (section.trim()) {
        const sectionNameEnd = section.indexOf("\n");
        const sectionName = section.substring(0, sectionNameEnd).trim();
        const sectionContent = section.substring(sectionNameEnd).trim();
        parsedCode[sectionName] = sectionContent;
      }
    });

    return parsedCode;
  };

  // Initialize code from backend or use templates
  useEffect(() => {
    const fetchCode = async () => {
      try {
        if (!userId) {
          console.error("No user ID found in sessionStorage");
          return;
        }

        const storageKey = `codebase_${codebaseId}_${framework}`;
        let storedCode = {};
        try {
          const storedValue = localStorage.getItem(storageKey);
          storedCode = storedValue ? JSON.parse(storedValue) : {};
        } catch (error) {
          console.warn("Invalid saved code found, resetting editor state.", error);
          localStorage.removeItem(storageKey);
          storedCode = {};
        }

        // Prefer a manually saved session from the DB over the /tmp backend copy
        if (Object.keys(storedCode).length === 0 && codebaseId) {
          try {
            const savedSession = await codebaseService.getSession(codebaseId);
            const sessionData = savedSession?.session_data;
            if (
              sessionData?.content &&
              sessionData.framework === framework &&
              Object.keys(sessionData.content).length > 0
            ) {
              storedCode = sessionData.content;
              localStorage.setItem(storageKey, JSON.stringify(storedCode));
            }
          } catch (error) {
            console.error("Error fetching saved session:", error);
          }
        }

        if (Object.keys(storedCode).length === 0) {
          try {
            const codeData = await codebaseService.getCode(
              userId,
              codebaseId,
              framework
            );

            if (
              codeData.code &&
              typeof codeData.code === "string" &&
              codeData.code !== "# New Codebase\n\n# Start coding here..."
            ) {
              const parsedCode = parseBackendCode(codeData.code);
              if (Object.keys(parsedCode).length > 0) {
                storedCode = parsedCode;
              } else {
                storedCode = { ...currentTemplates };
              }
            } else {
              storedCode = { ...currentTemplates };
            }
          } catch (error) {
            console.error("Error fetching from backend:", error);
            storedCode = { ...currentTemplates };
          }

          localStorage.setItem(storageKey, JSON.stringify(storedCode));
        }

        setContent(storedCode);
      } catch (error) {
        console.error("Error initializing code:", error);
        setContent({ ...currentTemplates });
      }
    };

    fetchCode();
  }, [codebaseId, framework, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save to backend with debounce
  useEffect(() => {
    if (!userId || !codebaseId || Object.keys(content).length === 0) return;

    setSaveStatus("unsaved");
    clearTimeout(backendSaveTimeoutRef.current);

    backendSaveTimeoutRef.current = setTimeout(async () => {
      try {
        setSaveStatus("saving");
        const fullCode = Object.keys(content)
          .map((s) => `# Section: ${s}\n${content[s]}`)
          .join("\n\n");

        await codebaseService.saveCode(userId, codebaseId, fullCode, framework);
        setSaveStatus("saved");
        setHasUnsavedChanges(false);
      } catch (err) {
        console.error("Auto-save to backend failed:", err);
        setSaveStatus("unsaved");
      }
    }, 2000);

    return () => clearTimeout(backendSaveTimeoutRef.current);
  }, [content]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save code to local storage and set unsaved flag
  const saveCode = (section, newValue) => {
    setContent((prev) => {
      const updatedContent = { ...prev, [section]: newValue };
      localStorage.setItem(
        `codebase_${codebaseId}_${framework}`,
        JSON.stringify(updatedContent)
      );
      return updatedContent;
    });
    setHasUnsavedChanges(true);
    setSaveStatus("unsaved");
  };

  // Switch framework (TensorFlow/PyTorch)
  const handleFrameworkSwitch = (newFramework) => {
    if (newFramework === framework) return;
    setFramework(newFramework);
    sessionStorage.setItem("framework", newFramework);
    setContent({});
  };

  // Reset a section to its template
  const resetSection = (sectionKey) => {
    const templateValue = currentTemplates[sectionKey];
    if (!templateValue) return;
    const editor = editorRefs.current[sectionKey];
    if (editor) {
      editor.setValue(templateValue);
    }
    saveCode(sectionKey, templateValue);
  };

  // Handle editor mount - set up refs and auto-save on change
  const handleEditorMount = (editor, monaco, section) => {
    editorRefs.current[section] = editor;
    monacoRefs.current[section] = monaco;

    editor.onDidChangeModelContent(() => {
      const currentValue = editor.getValue();
      clearTimeout(editor._saveTimeout);
      editor._saveTimeout = setTimeout(() => {
        saveCode(section, currentValue);
      }, 500);
    });
  };

  // Register autocomplete suggestions for Monaco editor
  const handleEditorDidMount = (editor, monaco) => {
    monaco.languages.registerCompletionItemProvider("python", {
      provideCompletionItems: (model, position) => {
        const suggestions = getImportSuggestions(framework).map((item) => ({
          label: item.label,
          kind: monaco.languages.CompletionItemKind.Snippet,
          insertText: item.insertText,
          range: {
            startLineNumber: position.lineNumber,
            startColumn: 1,
            endLineNumber: position.lineNumber,
            endColumn: position.column,
          },
        }));
        return { suggestions };
      },
    });
  };

  return {
    // State
    content,
    mode,
    framework,
    saveStatus,
    collapsedSections,
    fullscreenSection,
    currentTemplates,
    editorRefs,
    monacoRefs,

    // Setters
    setContent,
    setMode,
    setFramework,
    setSaveStatus,
    setCollapsedSections,
    setFullscreenSection,

    // Methods
    saveCode,
    handleFrameworkSwitch,
    resetSection,
    handleEditorMount,
    handleEditorDidMount,
    parseBackendCode,
  };
};

// Helper function to get import suggestions based on framework
export const getImportSuggestions = (framework) => {
  if (framework === "pytorch") {
    return [
      { label: "import torch", insertText: "import torch" },
      { label: "import torch.nn", insertText: "import torch.nn as nn" },
      {
        label: "import torch.optim",
        insertText: "import torch.optim as optim",
      },
      {
        label: "from torch.utils.data",
        insertText: "from torch.utils.data import DataLoader, TensorDataset",
      },
    ];
  }
  return [
    { label: "import tensorflow", insertText: "import tensorflow as tf" },
    {
      label: "from tensorflow.keras",
      insertText: "from tensorflow.keras import layers, models",
    },
    {
      label: "from tensorflow.keras.models",
      insertText: "from tensorflow.keras.models import Sequential",
    },
  ];
};
